/**
 * Configuration centralisée de l'application Famla
 * 
 * Ce fichier gère toutes les variables d'environnement avec des valeurs par défaut
 * appropriées pour chaque environnement (local, staging, production).
 * 
 * Usage:
 * import { config } from '../services/config';
 * console.log(config.api.baseUrl);
 */

// Types pour une meilleure sécurité de type
export type Environment = 'local' | 'test' | 'staging' | 'production';

export interface ApiConfig {
  baseUrl: string;
  audioBaseUrl: string;
  timeout: number;
  notificationWsUrl: string;
}

export interface AuthConfig {
  signInWithApple: string;
  signInWithGoogle: string;
  signInWithMicrosoft: string;
  cookieDomain: string;
  /** Cloudflare Turnstile site key (public). Empty = widget disabled until backend requires it. */
  turnstileSiteKey: string;
}

export interface AppConfig {
  name: string;
  version: string;
  environment: Environment;
  debug: boolean;
  enableAnalytics: boolean;
  enableTracking: boolean;
  stOnlyMode: boolean;
  /** Domaine email corporate SSO-only. Doit refléter ST_CORPORATE_DOMAIN côté API. */
  stCorporateDomain: string;
  /** Origine du portail SSO (sans slash final). Doit refléter ST_PORTAL_WEB côté API. */
  stPortalOrigin: string;
  /** Outlook taskpane: allow email + password login (alongside Microsoft). */
  officeEmailPasswordAuth: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════════════
// [WHITE LABELLING] Configuration Tenant Branding via variable JSON unique
// ═══════════════════════════════════════════════════════════════════════════════════
export interface TenantBranding {
  enabled: boolean;
  appName: string;
  logoUrl: string | null;
  logoLightUrl: string | null;
  logoDarkUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string;
  supportEmail: string;
}

export interface TenantBrandingDerived extends TenantBranding {
  gradient: string;
  exportText: string;
  footerText: string;
}

export interface Config {
  app: AppConfig;
  api: ApiConfig;
  auth: AuthConfig;
}

/**
 * Retire un commentaire inline type `valeur # commentaire` (souvent collé par erreur
 * dans .env et inclus tel quel par CRA/dotenv → URL cassée).
 */
const stripInlineEnvComment = (raw: string): string => {
  const hashIdx = raw.search(/\s+#/);
  return (hashIdx >= 0 ? raw.slice(0, hashIdx) : raw).trim();
};

/** Récupère une variable d'environnement avec une valeur par défaut */
const getEnvVar = (key: string, defaultValue: string): string => {
  const raw = process.env[key];
  if (raw === undefined || raw === '') return defaultValue;
  return stripInlineEnvComment(raw);
};

/**
 * Récupère une variable d'environnement boolean avec une valeur par défaut
 */
const getEnvBool = (key: string, defaultValue: boolean): boolean => {
  const value = process.env[key];
  if (value === undefined) return defaultValue;
  return stripInlineEnvComment(value).toLowerCase() === 'true';
};

/**
 * Récupère une variable d'environnement number avec une valeur par défaut
 */
const getEnvNumber = (key: string, defaultValue: number): number => {
  const value = process.env[key];
  if (value === undefined) return defaultValue;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Récupère une variable d'environnement spécifique à l'environnement avec suffixe
 * Essaie d'abord avec le suffixe de l'environnement, puis sans suffixe, puis la valeur par défaut
 */
const getEnvVarWithSuffix = (baseKey: string, environment: Environment, defaultValue: string): string => {
  const envKey = `${baseKey}_${environment.toUpperCase()}`;
  const valueWithSuffix = process.env[envKey];
  
  if (valueWithSuffix !== undefined) {
    const cleaned = stripInlineEnvComment(valueWithSuffix);
    // En développement, afficher d'où vient la valeur
    if (environment === 'local') {
      console.log(`[CONFIG] ${baseKey}: utilisé ${envKey} = "${cleaned}"`);
    }
    return cleaned;
  }
  
  // Fallback vers la variable sans suffixe
  const valueWithoutSuffix = process.env[baseKey];
  if (valueWithoutSuffix !== undefined) {
    const cleaned = stripInlineEnvComment(valueWithoutSuffix);
    if (environment === 'local') {
      console.log(`[CONFIG] ${baseKey}: fallback vers ${baseKey} = "${cleaned}"`);
    }
    return cleaned;
  }
  
  // Utilisation de la valeur par défaut
  if (environment === 'local') {
    console.warn(`[CONFIG] ${baseKey}: aucune variable trouvée, utilisation de la valeur par défaut = "${defaultValue}"`);
  }
  
  return defaultValue;
};

/**
 * Récupère une variable d'environnement boolean spécifique à l'environnement avec suffixe
 */
const getEnvBoolWithSuffix = (baseKey: string, environment: Environment, defaultValue: boolean): boolean => {
  const envKey = `${baseKey}_${environment.toUpperCase()}`;
  const valueWithSuffix = process.env[envKey];
  
  if (valueWithSuffix !== undefined) {
    const boolValue = valueWithSuffix.toLowerCase() === 'true';
    if (environment === 'local') {
      console.log(`[CONFIG] ${baseKey}: utilisé ${envKey} = ${boolValue}`);
    }
    return boolValue;
  }
  
  // Fallback vers la variable sans suffixe
  const valueWithoutSuffix = process.env[baseKey];
  if (valueWithoutSuffix !== undefined) {
    const boolValue = valueWithoutSuffix.toLowerCase() === 'true';
    if (environment === 'local') {
      console.log(`[CONFIG] ${baseKey}: fallback vers ${baseKey} = ${boolValue}`);
    }
    return boolValue;
  }
  
  // Utilisation de la valeur par défaut
  if (environment === 'local') {
    console.warn(`[CONFIG] ${baseKey}: aucune variable trouvée, utilisation de la valeur par défaut = ${defaultValue}`);
  }
  
  return defaultValue;
};

/**
 * Détermine l'environnement actuel
 */
const getCurrentEnvironment = (): Environment => {
  const env = getEnvVar('REACT_APP_ENV', 'local');
  const validEnvironments: Environment[] = ['local', 'test', 'staging', 'production'];
  
  if (validEnvironments.includes(env as Environment)) {
    return env as Environment;
  }
  
  console.warn(`Invalid environment "${env}", falling back to "local"`);
  return 'local';
};

/**
 * URLs de fallback absolu (utilisées uniquement si aucune variable d'environnement n'est trouvée)
 * Ces valeurs ne devraient normalement jamais être utilisées avec le système unifié
 * Valeurs basées sur l'historique du projet
 */
// const FALLBACK_API_URL = 'http://localhost:9540/api/v1';
// const FALLBACK_AUDIO_URL = 'http://localhost:9540/';

const FALLBACK_API_URL = 'https://testfl-services.famla.ai/api/v1';
const FALLBACK_AUDIO_URL = 'https://testfl-services.famla.ai/';
// Récupération de l'environnement actuel
const environment = getCurrentEnvironment();

/** En local, remplace localhost par 127.0.0.1 (Docker/Windows : localhost:9540 timeout IPv6). */
const normalizeLocalDevUrl = (url: string): string => {
  if (environment !== 'local') return url;
  return url.replace(/\/\/localhost:/gi, '//127.0.0.1:');
};

/**
 * Configuration principale de l'application
 */
export const config: Config = {
  app: {
    name: getEnvVar('REACT_APP_NAME', 'Famla'),
    version: getEnvVar('REACT_APP_VERSION', '0.1.0'),
    environment,
    debug: getEnvBoolWithSuffix('REACT_APP_DEBUG', environment, environment !== 'production'),
    enableAnalytics: getEnvBoolWithSuffix('REACT_APP_ENABLE_ANALYTICS', environment, environment === 'production'),
    enableTracking: getEnvBoolWithSuffix('REACT_APP_ENABLE_TRACKING', environment, environment === 'production'),
    // Configuration ST dédiée
    stOnlyMode: getEnvBool('REACT_APP_ST_ONLY_MODE', false),
    stCorporateDomain: getEnvVar('REACT_APP_ST_CORPORATE_DOMAIN', 'st.com').trim().toLowerCase(),
    stPortalOrigin: getEnvVar('REACT_APP_ST_PORTAL_ORIGIN', 'https://st.famla.ai').trim().replace(/\/+$/, ''),
    officeEmailPasswordAuth: getEnvBool('REACT_APP_OFFICE_EMAIL_PASSWORD_AUTH', true),
  },
  
  api: {
    baseUrl: normalizeLocalDevUrl(getEnvVarWithSuffix('REACT_APP_API_BASE_URL', environment, FALLBACK_API_URL)),
    audioBaseUrl: normalizeLocalDevUrl(getEnvVarWithSuffix('REACT_APP_AUDIO_BASE_URL', environment, FALLBACK_AUDIO_URL)),
    timeout: getEnvNumber('REACT_APP_API_TIMEOUT', 30000),
    notificationWsUrl: getEnvVarWithSuffix('REACT_APP_NOTIFICATION_WS_URL', environment, 'ws://localhost:8075'),
  },
  
  auth: {
    signInWithApple: getEnvVar('REACT_APP_SIGNIN_WITH_APPLE', 'https://appleid.apple.com'),
    signInWithGoogle: getEnvVar('REACT_APP_SIGNIN_WITH_GOOGLE', 'https://accounts.google.com'),
    signInWithMicrosoft: getEnvVar('REACT_APP_SIGNIN_WITH_MICROSOFT', 'https://login.microsoftonline.com'),
    cookieDomain: getEnvVar('REACT_APP_AUTH_COOKIE_DOMAIN', '.famla.ai'),
    // Clé de test Cloudflare (always passes) : 1x0000000000000000000000000000000AA
    turnstileSiteKey: getEnvVarWithSuffix('REACT_APP_TURNSTILE_SITE_KEY', environment, ''),
  }
};

/**
 * Fonctions utilitaires
 */
export const isProduction = (): boolean => config.app.environment === 'production';
export const isStaging = (): boolean => config.app.environment === 'staging';
export const isDevelopment = (): boolean => config.app.environment === 'local';
export const isTest = (): boolean => config.app.environment === 'test';
export const isDebugEnabled = (): boolean => config.app.debug;

/**
 * Logger conditionnel basé sur l'environnement
 */
export const logger = {
  debug: (message: string, ...args: any[]) => {
    if (isDebugEnabled()) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  },
  info: (message: string, ...args: any[]) => {
    console.info(`[INFO] ${message}`, ...args);
  },
  warn: (message: string, ...args: any[]) => {
    console.warn(`[WARN] ${message}`, ...args);
  },
  error: (message: string, ...args: any[]) => {
    console.error(`[ERROR] ${message}`, ...args);
  }
};

// Log de la configuration au démarrage (seulement en développement)
if (isDevelopment()) {
  logger.info('Application Configuration:', {
    environment: config.app.environment,
    apiBaseUrl: config.api.baseUrl,
    debug: config.app.debug
  });
}

// ═══════════════════════════════════════════════════════════════════════════════════
// [WHITE LABELLING] Parsing de la variable JSON REACT_APP_TENANT_BRANDING
// ═══════════════════════════════════════════════════════════════════════════════════

const DEFAULT_TENANT_BRANDING: TenantBranding = {
  enabled: false,
  appName: 'Famla',
  logoUrl: null,
  logoLightUrl: null,
  logoDarkUrl: null,
  faviconUrl: null,
  primaryColor: '#c7755c',
  supportEmail: 'contact@famla.ai'
};

function generateGradient(primaryColor: string): string {
  return `linear-gradient(90deg, ${primaryColor}, ${primaryColor})`;
}

function parseTenantBranding(): TenantBrandingDerived {
  const jsonStr = process.env.REACT_APP_TENANT_BRANDING;
  
  let base: TenantBranding = { ...DEFAULT_TENANT_BRANDING };
  
  if (jsonStr && jsonStr.trim() !== '') {
    try {
      const parsed = JSON.parse(jsonStr);
      base = { ...DEFAULT_TENANT_BRANDING, ...parsed };
      if (isDevelopment()) {
        logger.info('[TenantBranding] Configuration chargée depuis REACT_APP_TENANT_BRANDING');
      }
    } catch (e) {
      console.warn('[TenantBranding] JSON invalide dans REACT_APP_TENANT_BRANDING, utilisation des valeurs par défaut');
    }
  }
  
  const currentYear = new Date().getFullYear();
  
  return {
    ...base,
    gradient: generateGradient(base.primaryColor),
    exportText: `Powered by ${base.appName}`,
    footerText: `© ${currentYear} ${base.appName}`
  };
}

export const tenantBranding = parseTenantBranding();

if (isDevelopment() && tenantBranding.enabled) {
  logger.info('[TenantBranding] White labelling actif:', {
    appName: tenantBranding.appName,
    primaryColor: tenantBranding.primaryColor
  });
}

export default config;